import { BuProfileEntity } from "domain/entity/BuProfile/BuProfileEntity";
import { PrintQueueEntity } from "domain/entity/Common/PrintQueueEntity";
import { EMPTY_USER_ENTITY, UserEntity } from "domain/entity/User/UserEntity";
import { UserGroupEntity } from "domain/entity/userGroup/UserGroupEntity";
import { axiosGetData } from "domain/repository/axios/AxiosBasicImpl";
import authAxiosInstance from "domain/repository/axios/authAxiosInstanc";
import referenceAxiosInstance from "domain/repository/axios/referenceAxiosInstance";
import _ from "lodash";
import { AclType, AclTypeValue, excludePermissionListForAqct, excludePermissionListForOict, excludePermissionListForSapt, NbisivPermission } from "presentation/constant/ANAInfo/NbisivPermission";
import { Permission } from "presentation/constant/ANAInfo/PermissionName";
import { useUserVM } from "presentation/hook/User/useUserVM";

/**
 * Login assist function help to 
 * 1. retrieve the permission list by user name
 * 2. retrieve the operating companies by user name
 * 3. retrieve the BU list by user name
 * 3. retrieve the user by user name
 * 4. assist the system privilege control
 * @returns
 */
const useLoginAssist = () => {
    const userVM = useUserVM();
    /**
     * get normally user by user name
     * @param userName 
     * @returns 
     */
    const getUser = async (userName: string): Promise<UserEntity> => {
        //get user information
        const res = await axiosGetData(authAxiosInstance, `/v1/user/${userName}`, []).then((res) => {
            let user: UserEntity = EMPTY_USER_ENTITY;
            if (res.data) {
                user = res.data;
                return user;
            }
            return user;
        });
        return res;
    }

    /**
     * get user group and permission by user name
     * @param userName 
     * @returns 
     */
    const getUserGroup = async (userName: string): Promise<UserGroupEntity[]> => {
        //get user information
        const res = await axiosGetData(authAxiosInstance, `/v1/userGroupSearchByUser/${userName}`, []).then((res) => {
            let userGroups: UserGroupEntity[] = [];
            if (res.data) {
                userGroups = res.data;
                return userGroups;
            }
            return userGroups;
        });
        return res;
    }

    /**
     * Get permissions for support user
     * @param availableOperatingCompanies 
     * @returns 
     */
    const getPermissionsForSupport = async (userGroups: UserGroupEntity[], availableOperatingCompanies: any[]): Promise<{ [key: string]: NbisivPermission }> => {
        let permissions: { [key: string]: NbisivPermission } = {};

        if (userGroups) {
            //initial permission
            userGroups?.map((gp) => {
                gp.permissions?.map((pm) => {
                    let acl: AclTypeValue[] = permissions[pm.resource] ? (permissions[pm.resource].acl) : [];
                    if (pm.read && !acl.includes(AclType.READ)) {
                        acl.push(AclType.READ);
                    }
                    if (pm.create && !acl.includes(AclType.CREATE)) {
                        acl.push(AclType.CREATE);
                    }
                    if (pm.modify && !acl.includes(AclType.UPDATE)) {
                        acl.push(AclType.UPDATE);
                    }
                    if (pm.delete && !acl.includes(AclType.DELETE)) {
                        acl.push(AclType.DELETE);
                    }
                    const permission: NbisivPermission = {
                        acl: acl,
                        tml: availableOperatingCompanies
                    }
                    permissions[pm.resource] = permission;
                    return null;
                })
                return null;
            });
            return permissions;
        }
        return {};
    }

    /**
     * get Operating companies for support user
     * @param operatingUnit 
     * @returns 
     */
    const getOperatingCompaniesForSupport = (buProfile: BuProfileEntity) => {
        if (buProfile) {
            const companies = buProfile.buOperatingCompanies;
            if (companies && _.isArray(companies)) {
                const buList = companies.map((comp) => comp.operatingCompany ?? "");
                return buList;
            }
        }
        return [];
    }

    const getBuProfile = async (operatingUnit: string) => {
        const res = await axiosGetData(authAxiosInstance, `/v1/buprofile/bu/${operatingUnit}`, []);
        if (res) {
            if (res.data) {
                return res.data;
            }
        };
        return null;
    }

    /**
     * get permissions for normally user
     * @param user
     * @returns 
     */
    const getPermissionsForUser = async (userGroups: UserGroupEntity[]): Promise<{ [key: string]: NbisivPermission }> => {
        let permissions: { [key: string]: NbisivPermission } = {};

        if (userGroups) {
            //initial permission
            userGroups.forEach(group => {
                group.permissions?.forEach(permission => {
                    if (((permission.attributes?.includes('2473') || permission.attributes?.includes('2846')) && excludePermissionListForAqct.includes(permission.resource))
                        || ((permission.attributes?.includes('2817') || permission.attributes?.includes('2740')) && excludePermissionListForSapt.includes(permission.resource))
                        || ((permission.attributes?.includes('2764') || permission.attributes?.includes('2849')) && excludePermissionListForOict.includes(permission.resource))) {
                        return;
                    }

                    const acl: AclTypeValue[] = permissions[permission.resource] ? (permissions[permission.resource].acl) : [];
                    if (permission.read && !acl.includes(AclType.READ)) acl.push(AclType.READ);
                    if (permission.create && !acl.includes(AclType.CREATE)) acl.push(AclType.CREATE);
                    if (permission.modify && !acl.includes(AclType.UPDATE)) acl.push(AclType.UPDATE);
                    if (permission.delete && !acl.includes(AclType.DELETE)) acl.push(AclType.DELETE);

                    permissions[permission.resource] = {
                        acl,
                        tml: permission.attributes || []
                    };
                });
            });

            return permissions;
        }
        return {};
    }

    /**
     * get operating companies for normally user
     * @param permissions 
     * @returns 
     */
    const getOperatingCompaniseForUser = (permissions: { [key: string]: NbisivPermission }): string[] => {
        if (permissions) {
            const loginPermission = permissions[Permission.LOGIN];
            if (loginPermission) {
                return loginPermission.tml;
            }
        }
        return [];
    }


    /**
     * get printers for normally user
     * @returns 
     */
    const getPrinter = async (): Promise<PrintQueueEntity[]> => {
        return await axiosGetData(referenceAxiosInstance, `/v1/printQueueForCombox`, []).then(res => {
            const data = res.data;
            return data;
        }).catch(err => {
            return [];
        });
    }



    const updateLatestLoginDatetime = (userName: string) => {
        userVM.updateLatestLoginDatetime(userName);
    }


    return {
        getUser: getUser,
        getPermissionsForSupport: getPermissionsForSupport,
        getOperatingCompaniesForSupport: getOperatingCompaniesForSupport,
        getPermissionsForUser: getPermissionsForUser,
        getOperatingCompaniseForUser: getOperatingCompaniseForUser,
        getUserGroup: getUserGroup,
        updateLatestLoginDatetime: updateLatestLoginDatetime,
        getBuProfile: getBuProfile,
        getPrinter: getPrinter,
    }
}

export default useLoginAssist;